import React, { useState, useEffect } from 'react';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import { toast } from 'react-toastify';
import api from '../services/api';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirm_password: '',
    full_name: '',
    role: 'editor',
    department_id: ''
  });
  const [loading, setLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  
  // Pagination and Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    fetchUsers();
    fetchDepartments();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setOpenDropdownId(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await api.get('/auth/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await api.get('/departments');
      setDepartments(response.data);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      password: '',
      confirm_password: '',
      full_name: user.full_name,
      role: user.role,
      department_id: user.department_id || ''
    });
    setShowForm(true);
    setOpenDropdownId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!editingUser && (!formData.password || !formData.confirm_password)) {
      setPasswordError('Password is required for new users');
      return;
    }

    if (formData.password || formData.confirm_password) {
      if (formData.password !== formData.confirm_password) {
        setPasswordError('Passwords do not match');
        return;
      }
      if (formData.password.length < 6) {
        setPasswordError('Password must be at least 6 characters');
        return;
      }
    }

    setPasswordError('');
    setLoading(true);

    try {
      if (editingUser) {
        const updateData = {
          full_name: formData.full_name,
          role: formData.role,
          department_id: formData.department_id || null
        };

        if (formData.password) {
          updateData.password = formData.password;
        }

        const response = await api.put(`/auth/users/${editingUser.id}`, updateData);

        if (formData.password && response.data.email_sent) {
          toast.success('User updated! New credentials sent to email.');
        } else if (formData.password && !response.data.email_sent) {
          toast.warning('User updated but email failed to send.');
        } else {
          toast.success('User updated successfully!');
        }
      } else {
        const userData = {
          email: formData.email,
          password: formData.password,
          full_name: formData.full_name,
          role: formData.role,
          department_id: formData.department_id || null
        };

        const response = await api.post('/auth/register', userData);

        if (response.data.email_sent) {
          toast.success('User created! Credentials sent to email.');
        } else {
          toast.warning('User created but email failed to send. Please note the password.');
        }
      }

      setShowForm(false);
      setEditingUser(null);
      setFormData({
        email: '',
        password: '',
        confirm_password: '',
        full_name: '',
        role: 'editor',
        department_id: ''
      });
      fetchUsers();
      setCurrentPage(1);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error saving user');
      console.error('Save error:', error.response?.data);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateRole = async (userId, newRole, departmentId) => {
    try {
      await api.put(`/auth/users/${userId}/role`, { role: newRole, department_id: departmentId });
      toast.success('User role updated successfully');
      fetchUsers();
    } catch (error) {
      toast.error('Error updating user role');
    }
    setOpenDropdownId(null);
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      await api.delete(`/auth/users/${userId}`);
      toast.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      toast.error('Error deleting user');
    }
    setOpenDropdownId(null);
  };

  const handleResendEmail = async (userId) => {
    if (!window.confirm('Resend credentials to this user? A new password will be generated and sent.')) return;

    setActionLoading(true);
    try {
      const response = await api.post(`/auth/users/${userId}/resend-credentials`);
      if (response.data.email_sent) {
        toast.success('New credentials sent successfully! The user can now log in with the new password.');
      } else {
        toast.warning('Failed to send email. Please check email configuration.');
      }
    } catch (error) {
      toast.error('Error resending credentials');
      console.error('Resend error:', error.response?.data);
    } finally {
      setActionLoading(false);
    }
    setOpenDropdownId(null);
  };

  const handleResetPassword = async (userId) => {
    const newPassword = prompt('Enter new password for the user (min 6 characters):');
    if (!newPassword) return;
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    if (!window.confirm(`Set new password for this user and send via email?`)) return;

    setActionLoading(true);
    try {
      const user = users.find(u => u.id === userId);
      const response = await api.put(`/auth/users/${userId}`, {
        full_name: user?.full_name,
        role: user?.role,
        department_id: user?.department_id,
        password: newPassword
      });

      if (response.data.email_sent) {
        toast.success('Password reset successfully! New credentials sent to email.');
      } else {
        toast.warning('Password reset but email failed to send. Please provide the password to the user.');
      }
      fetchUsers();
    } catch (error) {
      toast.error('Error resetting password');
      console.error('Password reset error:', error.response?.data);
    } finally {
      setActionLoading(false);
    }
    setOpenDropdownId(null);
  };

  const openDropdown = (e, id) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setDropdownPosition({
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX - 180
    });
    setOpenDropdownId(openDropdownId === id ? null : id);
  };

  const getRoleBadgeColor = (role) => {
    switch(role) {
      case 'admin': return 'bg-red-100 text-red-800';
      case 'editor': return 'bg-blue-100 text-blue-800';
      case 'viewer': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Filter users
  const filteredUsers = users.filter(user => {
    const matchesSearch = searchTerm === '' ||
      user.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    
    return matchesSearch && matchesRole;
  });

  // Pagination
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, roleFilter]);

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">User Management</h1>
          <p className="text-gray-600 mt-1">Manage system users and their credentials</p>
        </div>
        <Button onClick={() => {
          setEditingUser(null);
          setFormData({
            email: '',
            password: '',
            confirm_password: '',
            full_name: '',
            role: 'editor',
            department_id: ''
          });
          setShowForm(true);
        }}>
          + Add New User
        </Button>
      </div>

      {showForm && (
        <Card className="mb-6">
          <h2 className="text-xl font-semibold mb-4">
            {editingUser ? 'Edit User' : 'Add New User'}
          </h2>
          <form onSubmit={handleSubmit}>
            <Input
              label="Full Name *"
              value={formData.full_name}
              onChange={(e) => setFormData({...formData, full_name: e.target.value})}
              required
            />

            {!editingUser && (
              <Input
                label="Email *"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                required
              />
            )}

            <Input
              label={editingUser ? "New Password (leave blank to keep current)" : "Password *"}
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({...formData, password: e.target.value})}
              required={!editingUser}
              placeholder={editingUser ? "Enter new password to change" : "Enter password"}
            />

            <Input
              label={editingUser ? "Confirm New Password" : "Confirm Password *"}
              type="password"
              value={formData.confirm_password}
              onChange={(e) => setFormData({...formData, confirm_password: e.target.value})}
              required={!editingUser}
            />

            {passwordError && (
              <p className="text-red-500 text-sm mb-2">{passwordError}</p>
            )}

            <div className="mb-4">
              <label className="label">Role *</label>
              <select
                value={formData.role}
                onChange={(e) => setFormData({...formData, role: e.target.value})}
                className="input-field"
                required
              >
                <option value="admin">Admin (All Departments)</option>
                <option value="editor">Editor</option>
                <option value="viewer">Viewer</option>
              </select>
            </div>

            {formData.role !== 'admin' && (
              <div className="mb-4">
                <label className="label">Department *</label>
                <select
                  value={formData.department_id}
                  onChange={(e) => setFormData({...formData, department_id: e.target.value})}
                  className="input-field"
                  required={formData.role !== 'admin'}
                >
                  <option value="">-- Select Department --</option>
                  {departments.map(dept => (
                    <option key={dept.id} value={dept.id}>{dept.name}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  User can only manage graduates from this department
                </p>
              </div>
            )}

            {formData.role === 'admin' && (
              <div className="mb-4">
                <label className="label">Department Access</label>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-sm text-gray-600">✅ All Departments (Admin Access)</p>
                  <p className="text-xs text-gray-500 mt-1">Admin users can manage all departments</p>
                </div>
              </div>
            )}

            <div className="flex gap-3 mt-4">
              <Button type="submit" disabled={loading}>
                {loading ? 'Saving...' : (editingUser ? 'Update User' : 'Create User')}
              </Button>
              <Button variant="outline" onClick={() => {
                setShowForm(false);
                setEditingUser(null);
              }}>
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      )}

      {/* Search and Filters - Fixed Alignment */}
      <div className="bg-white rounded-lg shadow mb-6 p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          {/* Search Field */}
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <input
              type="text"
              placeholder="🔍 Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
              style={{ height: '42px' }}
            />
          </div>

          {/* Role Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Role Filter</label>
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="w-full px-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white"
              style={{ height: '42px' }}
            >
              <option value="all">All Roles</option>
              <option value="admin">Admin</option>
              <option value="editor">Editor</option>
              <option value="viewer">Viewer</option>
            </select>
          </div>

          {/* Per Page */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Per Page</label>
            <select
              value={itemsPerPage}
              onChange={(e) => setItemsPerPage(Number(e.target.value))}
              className="w-full px-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white"
              style={{ height: '42px' }}
            >
              <option value={10}>10 per page</option>
              <option value={25}>25 per page</option>
              <option value={50}>50 per page</option>
              <option value={100}>100 per page</option>
            </select>
          </div>

          {/* Clear Filters Button */}
          <div>
            <button
              onClick={() => { 
                setSearchTerm(''); 
                setRoleFilter('all'); 
              }}
              className="w-full px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-all font-medium"
              style={{ height: '42px' }}
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white rounded-lg overflow-hidden shadow">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Department</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Login</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan="7" className="px-6 py-12 text-center">
                  <div className="flex justify-center items-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                    <span className="ml-2 text-gray-600">Loading users...</span>
                  </div>
                </td>
              </tr>
            ) : paginatedUsers.length === 0 ? (
              <tr>
                <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                  No users found
                </td>
              </tr>
            ) : (
              paginatedUsers.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{user.full_name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{user.email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <select
                      value={user.role}
                      onChange={(e) => handleUpdateRole(user.id, e.target.value, user.department_id)}
                      className={`px-2 py-1 text-xs rounded-full border-0 cursor-pointer ${getRoleBadgeColor(user.role)}`}
                    >
                      <option value="admin">Admin</option>
                      <option value="editor">Editor</option>
                      <option value="viewer">Viewer</option>
                    </select>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">
                      {user.role === 'admin' ? 'All Departments' : (user.department_name || '—')}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="relative">
                      <button
                        onClick={(e) => openDropdown(e, user.id)}
                        className="p-1 rounded-full hover:bg-gray-100 transition-colors"
                      >
                        <svg className="w-5 h-5 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mt-6">
          <div className="text-sm text-gray-700">
            Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredUsers.length)} of {filteredUsers.length} users
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-1 border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
            >
              Previous
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              return (
                <button
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`px-3 py-1 border rounded-md transition-colors ${
                    currentPage === pageNum
                      ? 'bg-primary text-white border-primary'
                      : 'hover:bg-gray-100'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
            <button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-3 py-1 border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Floating Dropdown Menu */}
      {openDropdownId && (
        <div
          className="fixed bg-white rounded-lg shadow-lg border z-50 py-1 min-w-[180px]"
          style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
        >
          {users.find(u => u.id === openDropdownId) && (
            <>
              <button
                onClick={() => handleEdit(users.find(u => u.id === openDropdownId))}
                className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 transition-colors"
              >
                <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Edit User
              </button>
              <button
                onClick={() => handleResetPassword(openDropdownId)}
                className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 transition-colors"
              >
                <svg className="w-4 h-4 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                </svg>
                Reset Password
              </button>
              <button
                onClick={() => handleResendEmail(openDropdownId)}
                className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 transition-colors"
              >
                <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Resend Credentials
              </button>
              <hr className="my-1" />
              <button
                onClick={() => handleDeleteUser(openDropdownId)}
                className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Delete User
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default Users;
